import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import {
  AppLayout,
  Header,
  Container,
  SpaceBetween,
  Button,
  Box,
  Badge,
  ColumnLayout,
  StatusIndicator,
  Table,
  TextFilter,
  Pagination,
  ProgressBar,
  Alert,
  Cards,
  Link,
} from "@cloudscape-design/components";
import TopNavigation from "./containers/TopNavigation/TopNavigation";
import Sidebar from "./containers/Sidebar/Sidebar";
import RightSidebar from "./containers/RightSidebar/RightSidebar";
import { ThemeSelector } from "./components/ThemeSelector/ThemeSelector";
import { applyTheme, getCurrentTheme, Theme } from "./themes/themeConfig";
import "./themes/global.themed.css";
import "./themes/navigation-force.css";
import HomePage from "./containers/homePage/HomePage";
import MonitorAlert from "./containers/MonitorAlert/MonitorAlert";
import IntelligentAnalysisReports from "./containers/IntelligentAnalysisReports/IntelligentAnalysisReports";
import ProcessOptimization from "./containers/ProcessOptimization/ProcessOptimization";
import { Hub } from "@aws-amplify/core";
import {
  getUserInfo,
  redirectToLoginPage,
  initializeSession,
} from "./utils/auth-provider";
import { useDispatch } from "react-redux";
import { pingRequest } from "./redux/actions/apiActions";
import { setUserInfo, clearUserInfo } from "./redux/actions/userActions";

// App Content Component with Router
const AppContent: React.FC = () => {
  const location = useLocation();
  const [chatState, setChatState] = useState<"closed" | "default" | "expanded">(
    "closed",
  );
  const [currentTheme, setCurrentTheme] = useState<Theme>(getCurrentTheme());
  const prevPathRef = React.useRef<string>(location.pathname);

  const handleChatStateChange = (
    newState: "closed" | "default" | "expanded",
  ) => {
    setChatState(newState);
  };

  useEffect(() => {
    const updateTheme = () => {
      const theme = getCurrentTheme();
      setCurrentTheme(theme);
      applyTheme(theme);

      // Apply theme to body and root
      document.body.style.backgroundColor = theme.colors.mainBackground;
      document.body.style.color = theme.colors.textPrimary;
    };

    // Listen for theme changes
    window.addEventListener("themeChanged", updateTheme);

    // Apply initial theme
    updateTheme();

    return () => {
      window.removeEventListener("themeChanged", updateTheme);
    };
  }, []);

  // Auto-expand chatbot on specific routes
  useEffect(() => {
    // Only trigger on actual route changes, not on state changes
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;

      if (
        location.pathname === "/analysis" ||
        location.pathname === "/reports"
      ) {
        setChatState("expanded");
      } else if (
        (location.pathname === "/monitor" ||
          location.pathname === "/optimization") &&
        chatState === "expanded"
      ) {
        setChatState("default");
      }
    }
  }, [location.pathname]);

  return (
    <div
      className="awsui"
      style={{
        backgroundColor: currentTheme.colors.mainBackground,
        color: currentTheme.colors.textPrimary,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}
      >
        <TopNavigation />
      </div>

      <div
        style={{
          display: "flex",
          flex: 1,
          overflow: "auto",
          position: "relative",
          marginTop: "56px", // Height of TopNavigation
        }}
      >
        <div
          style={{
            flex: 1,
            transition: "margin-right 0.3s ease-out",
            marginRight:
              chatState === "closed"
                ? "50px" // Leave space for Open AI button
                : chatState === "default"
                  ? "500px"
                  : "0px",
          }}
        >
          <AppLayout
            navigation={<Sidebar />}
            contentType="table"
            toolsHide={true}
            content={
              <div
                style={{
                  backgroundColor: currentTheme.colors.cardBackground,
                  color: currentTheme.colors.textPrimary,
                  minHeight: "100%",
                  width: "100%",
                  transition: "width 0.3s ease-out",
                  overflow: "visible",
                }}
              >
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/monitor" element={<MonitorAlert />} />
                  <Route
                    path="/analysis"
                    element={<IntelligentAnalysisReports />}
                  />
                  <Route
                    path="/reports"
                    element={<IntelligentAnalysisReports />}
                  />
                  <Route
                    path="/optimization"
                    element={<ProcessOptimization />}
                  />
                </Routes>
              </div>
            }
          />
        </div>

        <RightSidebar
          chatState={chatState}
          onChatStateChange={handleChatStateChange}
        />
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const dispatch = useDispatch();
  const [authState, setAuthState] = useState<string>("UNAUTHENTICATED");
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const hasInitialized = React.useRef<boolean>(false);

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // Initialize theme system on app startup
    const currentTheme = getCurrentTheme();
    applyTheme(currentTheme);

    // Apply initial body styling
    document.body.style.backgroundColor = currentTheme.colors.mainBackground;
    document.body.style.color = currentTheme.colors.textPrimary;
    document.body.style.fontFamily = currentTheme.colors.textPrimary
      ? "Amazon Ember, Helvetica Neue, Roboto, Arial, sans-serif"
      : "inherit";

    // Try to get user info on mount
    const handleUserSignIn = async () => {
      // Prevent multiple redirects
      if (isRedirecting) {
        return;
      }

      // Check if we're in OAuth callback flow
      const urlParams = new URLSearchParams(window.location.search);
      const hasCode = urlParams.has("code");

      if (hasCode) {
        return; // Don't call getUserInfo yet, wait for Hub signIn event
      }

      try {
        const userInfo = await getUserInfo();

        if (userInfo) {
          setAuthState("AUTHENTICATED");

          // Store user info in Redux (including tokens and expiration)
          
          // Determine username based on provider
          const providerName = userInfo.identities?.[0]?.providerName || "";
          let username = "";
          
          if (providerName === "LASSO") {
            // LASSO: use custom:username attribute
            username = userInfo["custom:username"] || "";
          } else if (providerName === "AmazonFederate") {
            // AmazonFederate: extract from email
            username = userInfo.email ? userInfo.email.split('@')[0] : "";
          } else {
            // Fallback for other providers
            username = userInfo.preferred_username || userInfo.identities?.[0]?.userId || "";
          }
          
          dispatch(
            setUserInfo({
              username: username,
              email: userInfo.email || "",
              sub: userInfo.sub,
              preferredUsername: userInfo.preferred_username,
              cognitoUsername: userInfo["cognito:username"],
              idToken: userInfo.idToken,
              accessToken: userInfo.accessToken,
              refreshToken: userInfo.refreshToken,
              tokenExpiration: userInfo.tokenExpiration,
              authTime: userInfo.authTime,
              familyName: userInfo.family_name || userInfo.familyName || "",
              givenName: userInfo.given_name || userInfo.givenName || "",
              provider: providerName,
            })
          );

          // Initialize session - DISABLED: We only use access token for API calls
          // Session timer was causing page refresh every 30 seconds
          // await initializeSession(
          //   () => {},
          //   (willExpire) => {},
          //   (user) => {},
          // );

          // Test ping endpoint
          dispatch(pingRequest());
        } else {
          setAuthState("UNAUTHENTICATED");
          dispatch(clearUserInfo());
          setIsRedirecting(true);
          redirectToLoginPage();
        }
      } catch (error) {
        console.error("[App] ERROR in handleUserSignIn:", error);
        setAuthState("UNAUTHENTICATED");
        dispatch(clearUserInfo());
        setIsRedirecting(true);
        redirectToLoginPage();
      }
    };

    // Listen to auth events
    Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signIn":
          setAuthState("AUTHENTICATED");
          handleUserSignIn();
          break;
        case "signIn_failure":
          setAuthState("SIGNIN_FAILURE");
          dispatch(clearUserInfo());
          break;
        case "signOut":
          dispatch(clearUserInfo());
          redirectToLoginPage();
          break;
        case "customOAuthState":
          window.location.href = decodeURIComponent(data);
          break;
      }
    });

    handleUserSignIn();
  }, [dispatch]); // IMPORTANT: Do NOT include isRedirecting in dependencies to avoid loops

  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
